import { AdminChatSocketEvents } from '@config/socketEvents';
import { uploadFile } from '@lib/api/uploads';
import {
  getAdminChatRoomId,
  isAdminChatMessageForRoom,
  isOutgoingUserChatMessage,
} from '@lib/chat/adminChatRoom';
import { logError } from '@lib/logError';
import {
  type ChangeEvent,
  type ClipboardEvent,
  useEffect,
  useRef,
  useState,
} from 'react';
import uuid from 'react-uuid';
import type { Socket } from 'socket.io-client';

import type { ChatUser, JoinedPayload } from './chatDialogsTypes';
import type { ChatMessage, ChatMessageStatus } from './chatMessageViewTypes';
import { applyUploadedLocation, messageFromFile } from './chatUpload';
import { useVoiceRecorder } from './useVoiceRecorder';

const pad = (value: number): string => String(value).padStart(2, '0');

const formatRecordingDuration = (totalSeconds: number): string => {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) {
    return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
  }
  return `${pad(minutes)}:${pad(seconds)}`;
};

const wireMessage = (message: ChatMessage): ChatMessage => {
  const { status: _status, ...rest } = message;
  void _status;
  return rest as ChatMessage;
};

const plainTextFromHtml = (html: string): string => {
  const withBreaks = html.replace(/<br\s*\/?>/gi, '\n');
  const doc = new DOMParser().parseFromString(withBreaks, 'text/html');
  return (doc.body.textContent ?? '').replace(/\u00a0/g, ' ').trim();
};

export const useChatSession = (chatUser: ChatUser, chatSocket: Socket) => {
  const voice = useVoiceRecorder();
  // Files kept until their upload succeeds, so a failed send can be retried
  const pendingFilesRef = useRef(
    new Map<string, { file: File; kind: string }>()
  );
  const [msgInputValue, setMgInputValue] = useState('');
  const [messageArray, setMessageArray] = useState<ChatMessage[]>([]);
  const [socketId, setSocketId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const roomId = getAdminChatRoomId(chatUser.id);
    const handleIsSeen = (data: ChatMessage): void => {
      if (!data.isSeen) {
        return;
      }
      setMessageArray((list) =>
        list.map((item) =>
          item.uuid === data.uuid ? { ...item, isSeen: true } : item
        )
      );
    };
    const handleMessage = (payload: unknown): void => {
      const data = payload as ChatMessage;
      if (!isAdminChatMessageForRoom(data, roomId)) {
        return;
      }
      if (!isOutgoingUserChatMessage(data, chatUser.id)) {
        setMessageArray((list) => [...list, data]);
      } else {
        handleIsSeen(data);
      }
    };
    const handleJoined = (payload: unknown): void => {
      const data = payload as JoinedPayload | null | undefined;
      if (data) {
        setSocketId(data.socketId ?? null);
        setMessageArray((current) => {
          if (!Array.isArray(data.chat)) {
            return current;
          }
          if (data.chat.length === 0 && current.length > 0) {
            return current;
          }
          return data.chat;
        });
      }
    };
    chatSocket.on(AdminChatSocketEvents.MESSAGE, handleMessage);
    chatSocket.on(AdminChatSocketEvents.JOINED, handleJoined);
    return () => {
      chatSocket.off(AdminChatSocketEvents.MESSAGE, handleMessage);
      chatSocket.off(AdminChatSocketEvents.JOINED, handleJoined);
    };
  }, [chatSocket, chatUser.id]);

  const setMessageStatus = (
    messageId: string,
    status: ChatMessageStatus | undefined,
    uploadedMessage?: ChatMessage
  ): void => {
    setMessageArray((list) =>
      list.map((item) =>
        item.uuid === messageId
          ? { ...(uploadedMessage ?? item), status }
          : item
      )
    );
  };

  const uploadAndSend = async (
    message: ChatMessage,
    file: File,
    kind: string
  ): Promise<void> => {
    const messageId = message.uuid as string;
    pendingFilesRef.current.set(messageId, { file, kind });
    setMessageStatus(messageId, 'sending');
    try {
      const uploadRes = await uploadFile(file, 'chat');
      const uploaded = structuredClone(wireMessage(message));
      applyUploadedLocation(uploaded, kind, uploadRes.location);
      chatSocket.emit(AdminChatSocketEvents.SEND_MESSAGE, uploaded);
      pendingFilesRef.current.delete(messageId);
      setMessageStatus(messageId, undefined, uploaded);
    } catch (error: unknown) {
      logError('chat upload', error);
      setMessageStatus(messageId, 'failed');
    }
  };

  const addAndUpload = (
    message: ChatMessage,
    file: File,
    kind: string
  ): void => {
    setMessageArray((list) => [...list, { ...message, status: 'sending' }]);
    void uploadAndSend(message, file, kind);
  };

  const retryMessage = (messageId: string): void => {
    const pending = pendingFilesRef.current.get(messageId);
    const message = messageArray.find((item) => item.uuid === messageId);
    if (!pending || !message) {
      return;
    }
    void uploadAndSend(message, pending.file, pending.kind);
  };

  const sendVoice = (): void => {
    const recording = voice.takePreview();
    if (!recording) {
      return;
    }
    const data: ChatMessage = {
      type: 'audio',
      audio: {
        duration: formatRecordingDuration(recording.durationSeconds),
        uri: recording.url,
      },
      user: { _id: chatUser.id },
      roomId: getAdminChatRoomId(chatUser.id),
      socketId: socketId,
      isSeen: false,
      uuid: uuid(),
      createdAt: new Date(),
    };
    addAndUpload(data, recording.file, 'audio');
  };

  const handleSend = (): void => {
    const text = plainTextFromHtml(msgInputValue);
    if (!text) {
      return;
    }
    const data: ChatMessage = {
      type: 'text',
      text,
      user: { _id: chatUser.id },
      roomId: getAdminChatRoomId(chatUser.id),
      socketId: socketId,
      isSeen: false,
      uuid: uuid(),
      createdAt: new Date(),
    };
    setMessageArray((list) => [...list, data]);
    setMgInputValue('');
    chatSocket.emit(AdminChatSocketEvents.SEND_MESSAGE, data);
  };

  const sendFile = (selectedFile: File): void => {
    const built = messageFromFile(selectedFile, {
      duration: formatRecordingDuration(0),
      userId: chatUser.id,
      roomId: getAdminChatRoomId(chatUser.id),
      socketId,
      messageId: uuid(),
    });
    if (!built) {
      return;
    }
    addAndUpload(built.message, selectedFile, built.kind);
  };

  const handleFileTypeField = (event: ChangeEvent<HTMLInputElement>): void => {
    const selectedFile = event.target.files?.[0];
    // Reset so picking the same file again still triggers a change
    event.target.value = '';
    if (selectedFile) {
      sendFile(selectedFile);
    }
  };

  const handlePaste = (event: ClipboardEvent<HTMLDivElement>): void => {
    const pastedFile = event.clipboardData.files[0];
    event.preventDefault();
    if (pastedFile) {
      sendFile(pastedFile);
      return;
    }
    // Insert plain text at the cursor instead of replacing the draft
    document.execCommand(
      'insertText',
      false,
      event.clipboardData.getData('text/plain')
    );
  };

  return {
    voice,
    sendVoice,
    fileInputRef,
    msgInputValue,
    setMgInputValue,
    messageArray,
    handleFileTypeField,
    handlePaste,
    handleSend,
    retryMessage,
  };
};
